// src/hooks/useRealtimeAvailability.js

// import { useMemo } from "react";
import { ref, set, get } from "firebase/database";
import { doc, getDoc } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { useAuth } from "./useAuth";
import { db as firestore } from '../firebase';
import { database } from '../firebase';
const BACKEND_URL =
  (process.env.NODE_ENV === "development"
    ? "http://localhost:4000"
    : "https://ai-realtor-2ktt.onrender.com");

// ─── Overlap helper ────────────────────────────────────────────────────────────
// Returns true if [aStart, aEnd) overlaps [bStart, bEnd)
const overlaps = (aStart, aEnd, bStart, bEnd) =>
  aStart < bEnd && aEnd > bStart;

const useRealtimeAvailability = () => {
  const { currentUser } = useAuth();

  const requireAuth = () => !!currentUser;

  // ─────────────────────────────────────────────────────────────────────────────
  // UNCHANGED: save / delete / read helpers used by CalendarScreen + AvailabilitySetter
  // ─────────────────────────────────────────────────────────────────────────────

  const saveAvailability = async (propertyId, newAvailabilities) => {
    if (!requireAuth()) return { error: "Not authenticated." };
    try {
      const path = `users/${currentUser.uid}/properties/${propertyId}/availability`;
      const availabilityRef = ref(database, path);
      const snap = await get(availabilityRef);
      const existing = snap.exists() ? snap.val() : [];
      const merged = [
        ...existing,
        ...newAvailabilities.map((a) => ({
          ...a,
          id: a.id ?? uuidv4(),
          isMaintenance: !!a.isMaintenance,
        })),
      ];
      await set(availabilityRef, merged);
      return merged;
    } catch (error) {
      console.error("Error in saveAvailability:", error);
      return { error };
    }
  };

  const deleteAvailability = async (propertyId, availabilityId) => {
    if (!requireAuth()) return { error: "Not authenticated." };
    try {
      const path = `users/${currentUser.uid}/properties/${propertyId}/availability`;
      const availabilityRef = ref(database, path);
      const snap = await get(availabilityRef);
      if (!snap.exists()) return [];
      const filtered = snap.val().filter((a) => a.id !== availabilityId);
      await set(availabilityRef, filtered);
      return filtered;
    } catch (error) {
      console.error("Error in deleteAvailability:", error);
      return { error };
    }
  };

  const getAvailability = async (propertyId) => {
    if (!requireAuth()) return [];
    try {
      const path = `users/${currentUser.uid}/properties/${propertyId}/availability`;
      const snap = await get(ref(database, path));
      return snap.exists() ? snap.val() : [];
    } catch (error) {
      console.error("Error in getAvailability:", error);
      return [];
    }
  };

  const getAvailabilityForAllProperties = async () => {
    if (!requireAuth()) return [];
    try {
      const propsRef = ref(database, `users/${currentUser.uid}/properties`);
      const snap = await get(propsRef);
      if (!snap.exists()) return [];
      const out = [];
      Object.entries(snap.val()).forEach(([propertyId, val]) => {
        (val.availability ?? []).forEach((a) => out.push({ ...a, propertyId }));
      });
      return out;
    } catch (error) {
      console.error("Error in getAvailabilityForAllProperties:", error);
      return [];
    }
  };

  const fetchUserProperties = async () => {
    if (!requireAuth()) return [];
    try {
      const listings = ref(database, `users/${currentUser.uid}/listings`);
      const snap = await get(listings);
      if (!snap.exists()) return [];
      const ids = Object.keys(snap.val());
      const props = await Promise.all(
        ids.map(async (id) => {
          const s = await get(ref(database, `listings/${id}`));
          return s.exists() ? { id, ...s.val() } : null;
        })
      );
      return props.filter(Boolean);
    } catch (error) {
      console.error("Error in fetchUserProperties:", error);
      return [];
    }
  };

  const getAllEvents = async () => {
    if (!requireAuth()) return [];
    try {
      const eventsRef = ref(database, `users/${currentUser.uid}/events`);
      const snap = await get(eventsRef);
      return snap.exists() ? snap.val() : [];
    } catch (error) {
      console.error("Error in getAllEvents:", error);
      return [];
    }
  };

  const saveEvent = async (event) => {
    if (!requireAuth()) return { error: "Not authenticated." };
    try {
      const eventsRef = ref(database, `users/${currentUser.uid}/events`);
      await set(eventsRef, event);
      return event;
    } catch (error) {
      console.error("Error in saveEvent:", error);
      return { error };
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // PUBLIC AVAILABILITY LOOK-UP  ← R2 changes live here
  // Called by SingleListingPage to populate the time-slot picker for tenants.
  // ─────────────────────────────────────────────────────────────────────────────

  const getAvailableTimesForProperty = async (propertyId) => {
    try {
      // ── Step 0: find the listing + owner ──────────────────────────────────
      const propSnap = await get(ref(database, `listings/${propertyId}`));
      if (!propSnap.exists()) return [];

      const listing = propSnap.val();
      const ownerId = listing.ownerId;
      if (!ownerId) return [];

      // ── Step 1: fetch raw availability for this property ──────────────────
      const availRef = ref(
        database,
        `users/${ownerId}/properties/${propertyId}/availability`
      );
      const availSnap = await get(availRef);
      if (!availSnap.exists()) return [];

      let slots = availSnap.val().filter((a) => !a.isMaintenance);
      if (!slots.length) return [];

      // ── Filter A: cross-property confirmed-booking conflict check ─────────
      // Collect all confirmed bookings across every property this owner has,
      // then remove any availability slot that overlaps one of those bookings.
      try {
        const allPropsSnap = await get(
          ref(database, `listings`)
        );

        // Gather all listing IDs that belong to this owner
        const ownerListingIds = [];
        if (allPropsSnap.exists()) {
          Object.entries(allPropsSnap.val()).forEach(([id, data]) => {
            if (data.ownerId === ownerId) ownerListingIds.push(id);
          });
        }

        // Fetch confirmed bookings for every owner listing (including other properties)
        const confirmedRanges = []; // [{ start: ms, end: ms }]

        await Promise.all(
          ownerListingIds.map(async (listingId) => {
            try {
              const reqsSnap = await get(
                ref(database, `listings/${listingId}/viewingRequests`)
              );
              if (!reqsSnap.exists()) return;

              Object.values(reqsSnap.val()).forEach((req) => {
                // Only block on confirmed / approved bookings
                if (req.status !== "approved" && req.status !== "confirmed") return;

                const details = req.dateDetails;
                if (!details) return;

                // Prefer explicit startTime/endTime; fall back to a 1-hour window
                if (details.startTime) {
                  const start = new Date(details.startTime).getTime();
                  const end = details.endTime
                    ? new Date(details.endTime).getTime()
                    : start + 60 * 60 * 1000;
                  confirmedRanges.push({ start, end });
                }
              });
            } catch {
              // Non-critical — skip individual listing on error
            }
          })
        );

        if (confirmedRanges.length > 0) {
          slots = slots.filter((slot) => {
            if (!slot.startTime || !slot.endTime) return true; // keep if no times
            const slotStart = new Date(slot.startTime).getTime();
            const slotEnd = new Date(slot.endTime).getTime();
            // Keep the slot only if it doesn't overlap ANY confirmed booking
            return !confirmedRanges.some((r) =>
              overlaps(slotStart, slotEnd, r.start, r.end)
            );
          });
        }
      } catch (filterAErr) {
        // Filter A failure is non-fatal — continue with unfiltered slots
        console.warn("[getAvailableTimesForProperty] Filter A error:", filterAErr.message);
      }

      // ── Filter B: Google Calendar FreeBusy conflict check (R5 graceful) ──
      try {
        // Determine the window to query: earliest slot start → latest slot end
        const slotTimes = slots
          .filter((s) => s.startTime && s.endTime)
          .map((s) => ({
            start: new Date(s.startTime).getTime(),
            end: new Date(s.endTime).getTime(),
          }));

        if (slotTimes.length > 0) {
          const windowStart = new Date(Math.min(...slotTimes.map((t) => t.start)));
          const windowEnd = new Date(Math.max(...slotTimes.map((t) => t.end)));

          const response = await fetch(`${BACKEND_URL}/google-calendar/busy`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              uid: ownerId,
              timeMin: windowStart.toISOString(),
              timeMax: windowEnd.toISOString(),
            }),
          });

          if (response.ok) {
            const { busy, connected } = await response.json();

            // connected: false means agent hasn't linked GCal → skip silently (R5)
            if (connected !== false && busy && busy.length > 0) {
              slots = slots.filter((slot) => {
                if (!slot.startTime || !slot.endTime) return true;
                const slotStart = new Date(slot.startTime).getTime();
                const slotEnd = new Date(slot.endTime).getTime();
                return !busy.some((b) =>
                  overlaps(
                    slotStart,
                    slotEnd,
                    new Date(b.start).getTime(),
                    new Date(b.end).getTime()
                  )
                );
              });
            }
          }
        }
      } catch (filterBErr) {
        // Filter B failure is always non-fatal — R5 requirement
        console.warn("[getAvailableTimesForProperty] Filter B (GCal) error:", filterBErr.message);
      }

      return slots;
    } catch (error) {
      console.error("Error in getAvailableTimesForProperty:", error);
      return [];
    }
  };

  const getUserOnboarding = async () => {
    if (!requireAuth()) return null;
    try {
      const docSnap = await getDoc(doc(firestore, "users", currentUser.uid));
      return docSnap.exists() ? docSnap.data().onboarding : null;
    } catch (error) {
      console.error("Error in getUserOnboarding:", error);
      return null;
    }
  };

  return {
    saveAvailability,
    deleteAvailability,
    getAvailability,
    getAvailabilityForAllProperties,
    fetchUserProperties,
    getAllEvents,
    saveEvent,
    getAvailableTimesForProperty,
    getUserOnboarding,
  };
};

export default useRealtimeAvailability;